import type { AuthClient } from './client';

/**
 * Fetch wrapper that injects `Authorization: Bearer <token>` on every request
 * and silently retries once after a token refresh on 401.
 *
 * One strategy for every consumer: always resolves the credential through
 * `client.getPrimaryToken()` (never a hardcoded access-token accessor), so the
 * same wrapper is correct whether the client was configured for the access
 * token (operations) or the ID token (customer).
 *
 * Does NOT redirect to login — that is each app's route guard's job (see
 * `client.silentRefreshOrRedirect()`). Keeping login redirect out of the fetch
 * wrapper eliminates PKCE verifier race conditions from concurrent callers.
 */
export function createAuthFetch(client: AuthClient): typeof fetch {
  return async (input, init) => {
    const token = client.getPrimaryToken();
    const headers = new Headers(init?.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    let res = await fetch(input, { ...init, headers });

    if (res.status === 401) {
      const refreshed = await client.refreshAccessToken();
      if (refreshed) {
        const newToken = client.getPrimaryToken();
        if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
        res = await fetch(input, { ...init, headers });
      }
      // If still 401 after refresh attempt, return it. Callers show error
      // state; next navigation re-triggers the route guard, which redirects to login.
    }

    return res;
  };
}
